import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ProjectCard from './ProjectCard';

const ProjectGrid = ({ projects, onEdit, onDuplicate, onDelete, onView, onCreateNew }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const statusOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'draft', label: 'Draft' },
    { value: 'completed', label: 'Completed' },
    { value: 'rendering', label: 'Rendering' }
  ];

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'name', label: 'Name A-Z' },
    { value: 'status', label: 'Status' },
    { value: 'credits', label: 'Credits Count' }
  ];

  const filteredProjects = projects?.filter(project => {
      const matchesSearch = project?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          project?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = statusFilter === 'all' || project?.status === statusFilter;
      return matchesSearch && matchesStatus;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'status':
          return a?.status?.localeCompare(b?.status);
        case 'credits':
          return b?.creditsCount - a?.creditsCount;
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  return (
    <div className="space-y-4">
      {/* Header with Search and Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <Input
            type="search"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full sm:w-64"
          />
          
          <div className="flex space-x-3">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-32"
            />
            
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              className="w-32"
            />
          </div>
        </div>

        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={onCreateNew}
        >
          New Project
        </Button>
      </div>
      {/* Projects Grid */}
      {filteredProjects?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FolderOpen" size={24} className="text-muted-foreground" />
          </div>
          
          {projects?.length === 0 ? (
            <>
              <h3 className="text-lg font-semibold text-foreground mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-6">Create your first film credit project to get started</p>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={onCreateNew}
              >
                Create First Project
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-foreground mb-2">No matching projects</h3>
              <p className="text-muted-foreground mb-6">Try adjusting your search or filter criteria</p>
              <Button
                variant="outline"
                iconName="X"
                iconPosition="left"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Results Count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredProjects?.length} of {projects?.length} projects
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProjects?.map((project) => (
              <ProjectCard
                key={project?.id}
                project={project}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onView={onView}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectGrid;